import React from "react";
import { cn } from "@/lib/utils";

const STAR_INDICES = ["one", "two", "three", "four", "five"];

/* Five-star row */
function Stars({ color = "#FFC107" }) {
    return (
        <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
            {STAR_INDICES.map((position) => (
                <svg
                    key={`star-${position}`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    aria-hidden="true"
                    fill={color}
                >
                    <path d="M12 2l2.9 6.95 7.5.65-5.7 4.95 1.75 7.35L12 18.27 5.55 21.9l1.75-7.35L1.6 9.6l7.5-.65L12 2z" />
                </svg>
            ))}
        </div>
    );
}

function GoogleG({ className = "w-5 h-5" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
            <path fill="#4285F4" d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.385a4.604 4.604 0 0 1-1.997 3.022v2.51h3.232c1.891-1.742 2.98-4.305 2.98-7.355z" />
            <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.618-2.418l-3.232-2.51c-.896.6-2.042.955-3.386.955-2.605 0-4.812-1.76-5.6-4.125H3.07v2.59A9.997 9.997 0 0 0 12 22z" />
            <path fill="#FBBC05" d="M6.4 13.902a5.997 5.997 0 0 1 0-3.804V7.508H3.07a10.003 10.003 0 0 0 0 8.984l3.33-2.59z" />
            <path fill="#EA4335" d="M12 5.977c1.47 0 2.787.505 3.823 1.495l2.868-2.868C16.96 3.022 14.696 2 12 2 8.146 2 4.823 4.21 3.07 7.508l3.33 2.59C7.188 7.737 9.395 5.977 12 5.977z" />
        </svg>
    );
}

function FacebookF({ className = "w-5 h-5" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="#1877F2" />
            <path
                fill="#ffffff"
                d="M13.5 22v-7.5h2.5l.38-2.9h-2.88V9.84c0-.84.23-1.41 1.43-1.41h1.53V5.83A20 20 0 0 0 14.24 5.7c-2.2 0-3.7 1.34-3.7 3.8v2.1H8v2.9h2.54V22h2.96z"
            />
        </svg>
    );
}

function TrustpilotMark({ className = "w-5 h-5" }) {
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
            <path
                fill="#00B67A"
                d="M12 2l2.9 6.95 7.5.65-5.7 4.95 1.75 7.35L12 18.27 5.55 21.9l1.75-7.35L1.6 9.6l7.5-.65L12 2z"
            />
        </svg>
    );
}

const PROVIDERS = [
    { name: "Google", Icon: GoogleG },
    { name: "Facebook", Icon: FacebookF },
    { name: "Trustpilot", Icon: TrustpilotMark },
];

/**
 * ReviewBadges — three platform marks each with 5 stars beneath.
 * No numerical ratings. Visual trust strip only.
 */
export function ReviewBadges({
    tone = "light",
    className = "",
    testId = "review-badges",
}) {
    const isDark = tone === "dark";
    return (
        <div
            data-testid={testId}
            className={cn(
                "inline-flex flex-wrap items-center gap-x-5 gap-y-3 sm:gap-x-7",
                className,
            )}
        >
            {PROVIDERS.map(({ name, Icon }, i) => (
                <React.Fragment key={name}>
                    <div
                        className="flex items-center gap-2.5"
                        data-testid={`review-badge-${name.toLowerCase()}`}
                    >
                        <Icon className="w-5 h-5 shrink-0" />
                        <div className="flex flex-col items-start leading-none">
                            <span
                                className={cn(
                                    "font-display font-bold text-[13px] sm:text-sm",
                                    isDark ? "text-white" : "text-[#0a0b0d]",
                                )}
                            >
                                {name}
                            </span>
                            <span className="mt-1">
                                <Stars />
                            </span>
                        </div>
                    </div>
                    {i < PROVIDERS.length - 1 ? (
                        <span
                            className={cn(
                                "hidden sm:inline-block h-8 w-px",
                                isDark ? "bg-white/20" : "bg-[#0a0b0d]/15",
                            )}
                            aria-hidden="true"
                        />
                    ) : null}
                </React.Fragment>
            ))}
        </div>
    );
}

export default ReviewBadges;
